import { JSON } from '@symon-ai/wisepipe-serialize';
import base64url from 'base64url';
import { randomBytes } from 'crypto';
import * as uuidBuffer from 'uuid-buffer';
import { decodeToken } from './continuationToken';
import { S3File } from './s3File';
import { UiDomains } from './uiDomains';
export class Utils {
    static getEnvVariable(name) {
        const value = process.env[name];
        if (!value)
            throw new Error(`${name} environment variable not set.`);
        return value;
    }
    static sleep(ms) {
        return new Promise((resolve) => {
            setTimeout(resolve, ms);
        });
    }
    static ddbByteSize(json) {
        // https://docs.aws.amazon.com/amazondynamodb/latest/developerguide/CapacityUnitCalculations.html
        // An attribute of type List or Map requires 3 bytes of overhead, regardless of its contents.
        let size = 3;
        const isArray = Array.isArray(json);
        for (const [key, value] of Object.entries(json)) {
            // (length of attribute name) - Only add the attribute name if the json is an Map/Object.
            if (!isArray) {
                size += key.length;
            }
            // The size of a string is (number of UTF-8-encoded bytes).
            if (typeof value === 'string') {
                size += Buffer.byteLength(value, 'utf-8');
            }
            // Numbers are variable length, with up to 38 significant digits. Leading and trailing zeroes are trimmed.
            // The size of a number is approximately (1 byte per two significant digits) + (1 byte)
            else if (typeof value === 'number') {
                size += 1 + Math.round(value.toString().length / 2);
            }
            // The size of a null attribute or a Boolean attribute is (1 byte).
            else if (typeof value === 'boolean' || !value) {
                size += 1;
            }
            // The size of a List or Map is sum (size of nested elements)
            else {
                size += Utils.ddbByteSize(JSON.deserialize(value));
            }
        }
        return size;
    }
    static isValidEmailAddress(email) {
        return /^[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);
    }
    static isVailduuid(uuidToValidate) {
        return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(uuidToValidate);
    }
    // Apparently the stream parameter should be of type Readable|ReadableStream|Blob
    // The latter 2 don't seem to exist anywhere.
    static async streamToString(stream) {
        return await new Promise((resolve, reject) => {
            const chunks = [];
            stream.on('data', (chunk) => chunks.push(chunk));
            stream.on('error', reject);
            stream.on('end', () => resolve(Buffer.concat(chunks).toString('utf-8')));
        });
    }
    /** Removes a single item from an array */
    static removeArrayItem(array, item) {
        if (!array) {
            return;
        }
        const itemIndex = array.indexOf(item);
        if (itemIndex >= 0) {
            array.splice(itemIndex, 1);
        }
    }
    /** Add a flag to Array (add only once) */
    static addFlagToArray(array, item) {
        if (!array) {
            return;
        }
        const itemIndex = array.indexOf(item);
        if (itemIndex < 0) {
            array.push(item);
        }
    }
    static toBuffer(buffer) {
        let bufferID;
        if (typeof buffer === 'string') {
            bufferID = Buffer.from(buffer, 'base64');
        }
        else {
            bufferID = Buffer.from(buffer);
        }
        return bufferID;
    }
    static listToMap(objects, keyProp) {
        return objects.reduce((map, obj) => {
            map.set(keyProp(obj), obj);
            return map;
        }, new Map());
    }
    static listToSet(objects, keyProp) {
        return objects.reduce((set, obj) => {
            set.add(keyProp(obj));
            return set;
        }, new Set());
    }
    // use any[] so we don't have to import s3 into this package
    static sortS3PartFiles(prefix, extraParts, bucket) {
        const partLocations = [];
        if (extraParts) {
            // sort it so we can read the files in order; would help python, but not spark.  but python likely won't even have parts
            extraParts.sort((a, b) => {
                const [aMajorPart, aMinorPart] = a.Key.substring(prefix.length).split('.');
                const [bMajorPart, bMinorPart] = b.Key.substring(prefix.length).split('.');
                if (aMajorPart < bMajorPart) {
                    return -1;
                }
                else if (aMajorPart > bMajorPart) {
                    return 1;
                }
                else if (aMinorPart < bMinorPart) {
                    return -1;
                }
                else if (aMinorPart > bMinorPart) {
                    return 1;
                }
                else {
                    return 0;
                }
            });
            for (const extraPart of extraParts) {
                partLocations.push(new S3File(bucket, extraPart.Key));
            }
        }
        return partLocations;
    }
    static bufferID(orgID) {
        return Utils.uuidStringToBuffer(orgID);
    }
    static orgID(buffer) {
        return Utils.uuidUintArrayToString(buffer);
    }
    static uuidUintArrayToString(buffer) {
        const bufferID = Utils.toBuffer(buffer);
        const str = uuidBuffer.toString(bufferID);
        return str;
    }
    static uuidStringToBuffer(str) {
        const bufferID = uuidBuffer.toBuffer(str);
        return bufferID;
    }
    static base64StringToBuffer(base64ID) {
        return base64url.toBuffer(base64ID);
    }
    // use for elementIDs, pipeIDs
    static base64BufferToString(bufferID) {
        return base64url.encode(bufferID);
    }
    static newID(length = 9) {
        return base64url.encode(randomBytes(length));
    }
    static isTestNodeEnv() {
        return process.env.NODE_ENV === 'test';
    }
    static ifNotTestNodeEnv(t, notFn, fn = (t) => t) {
        return Utils.isTestNodeEnv() ? fn(t) : notFn(t);
    }
    static async getAllItemsWithToken(getAllPromise, args) {
        let token;
        let version;
        let items = [];
        do {
            // Expected that encoding happens inside store.ts
            const pagedItems = await getAllPromise(...args, token, version);
            items = [...items, ...pagedItems.items];
            const tokens = pagedItems.continuationToken ? decodeToken(pagedItems.continuationToken) : undefined;
            token = tokens?.token;
            version = tokens ? tokens.version : 2;
        } while (token);
        return items;
    }
    static async getAllItemsWithMultiToken(getAllPromise, params) {
        let tokenV1;
        let tokenV2;
        let version;
        let items = [];
        do {
            // Expected that encoding happens inside store.ts
            // eslint-disable-next-line no-await-in-loop
            const pagedItems = await getAllPromise(...params, tokenV1, tokenV2, version);
            items = [...items, ...pagedItems.items];
            const tokens = pagedItems.continuationToken ? decodeToken(pagedItems.continuationToken) : undefined;
            tokenV1 = tokens?.token;
            tokenV2 = tokens?.tokenV2;
            version = tokens ? tokens.version : 2;
        } while (tokenV1 && tokenV2);
        return items;
    }
    /**
     * Get current date/time
     * @returns Date object set to the system Date
     * @description This method can be mocked for testing purposes with jest when it comes to testing methods that capture current time while creating new objects (please refer to its usages in test files)
     */
    static getCurrentDate() {
        return new Date();
    }
    // use Utils.retryReturn.call(this, xxxx) so that the errHandler and retryFunction has the right context
    // check gremlinClient.ts/lineageService for examples
    static async retryReturn(retryFunc, 
    // returns true to retry, false to throw; make sure this method exists in same "this" context as the caller func
    // check lineageService.ts for example of that
    errHandler, methodName, times = 5, interval = 1000, 
    // expBackoffFunc is a function that takes the current retry count and interval and returns a timeout
    // Returns the interval by default in case we don't want to use the function
    expBackoffFunc = (retryCount, interval) => interval) {
        for (let i = 0; i < times; i++) {
            try {
                // eslint-disable-next-line no-await-in-loop
                return await retryFunc.call(this);
            }
            catch (err) {
                // eslint-disable-next-line no-await-in-loop
                if (await errHandler.call(this, err)) {
                    console.log(`Error calling ${methodName}: ${err}, retrying ${i + 1}`);
                    await Utils.sleep(expBackoffFunc(i, interval));
                    continue;
                }
                throw err;
            }
        }
        throw new Error(`Err calling ${methodName}, exceeded retries`);
    }
    static getIntQueryParam(event, paramName, defaultVal) {
        return event.queryStringParameters?.[paramName] ? parseInt(event.queryStringParameters[paramName]) : defaultVal;
    }
    static getBoolQueryParam(event, paramName, defaultVal = true) {
        return event.queryStringParameters?.[paramName]
            ? JSON.parse(event.queryStringParameters[paramName])
            : defaultVal;
    }
    static replaceAt(str, start, end, replacement) {
        if (start < 0 || end < 0 || start > end || end > str.length) {
            throw new Error('Invalid parameters');
        }
        if (!str)
            throw new Error('Undefined string');
        if (!replacement)
            throw new Error('Undefined replacement');
        return str.substring(0, start) + replacement + str.substring(end);
    }
    static truncate(str, length) {
        if (!str)
            throw new Error('Undefined string');
        const result = str.length > length ? `${str.substring(0, length - 3)}...` : str;
        return result;
    }
    static getUiDomain(hostname) {
        const subDomain = hostname.split('.')[0];
        const eltRegex = /^elt-|-elt$|^elt$/i;
        const aiRegex = /^ai-|-ai$|^ai$/i;
        const embeddedRegex = /-embedded$/i;
        if (eltRegex.test(subDomain)) {
            return UiDomains.ELT;
        }
        else if (aiRegex.test(subDomain)) {
            return UiDomains.AI;
        }
        else if (embeddedRegex.test(subDomain)) {
            return UiDomains.EMBEDDED;
        }
        else {
            return UiDomains.SYMON;
        }
    }
    static getDomainFromUserAttribute(attr) {
        switch (attr) {
            case 'ELT':
                return UiDomains.ELT;
            case 'AI':
                return UiDomains.AI;
            default:
                return UiDomains.SYMON;
        }
    }
    static parseJsonFile(json, parseItem) {
        const result = [];
        // 1MB max for single value
        const accumulator = { curSize: 0 };
        // It could be a array of json or a json per line
        if (json.startsWith('[') &&
            (json.endsWith(']') || json.endsWith(']\n') || json.endsWith(']\r') || json.endsWith(']\r\n'))) {
            const items = JSON.parse(json);
            items.forEach((item) => {
                result.push(parseItem(item, accumulator));
            });
        }
        else {
            const lines = json.split(/\r?\n/);
            lines.forEach((line) => {
                if (line) {
                    const item = JSON.parse(line);
                    result.push(parseItem(item, accumulator));
                }
            });
        }
        return result;
    }
    static toPyJsonString(target) {
        const json = JSON.stringify(target, null, -1);
        // If we put JSON inside a string in a python program then all backslashes will end up operating on the next
        // character and disappearing (e.g. \" => "). To preserve the meaning we need to make sure the backslashes to
        // make it into the string where they can be interpreted by the receiving JSON parser, by doubling them up.
        const backslashes = json.replace(/\\/g, '\\\\');
        // Escape all single quotes within the string since we're using single quote to wrap the entire string. Here
        // we actually do want the backslash to escape the next character so we need to do this after the line above.
        const escaped = backslashes.replace(/'/g, "\\'");
        return `'${escaped}'`;
    }
    // this function finds the value in the input string and returns true if it is isolated
    // this is used for formula to identify if a value is a function name or a text in a string
    // specifically used on calc services to see if now() is called in the function
    static findIsolatedValueOutsideQuotes(input, value) {
        // Escape special characters in the value to prevent unintended regex behavior
        const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Regular expression to match isolated `value` outside of quotes
        // Matches `value` that is either at the start of the string, preceded or followed by whitespace, and ignores quoted sections
        const pattern = new RegExp(`(^|\\s)${escapedValue}($|\\s)(?=(?:[^'"]*['"][^'"]*['"])*[^'"]*$)`);
        return pattern.test(input.trim());
    }
}
//# sourceMappingURL=utils.js.map